import React, { useState, useEffect } from 'react';
import { apiFetch, getApiBaseUrl } from '../../config/api';
import { APP_VERSION } from '../../config/constants';

const HealthCheck = () => {
  const [healthData] = useState({
    status: 'OK',
    service: 'Llamasee Frontend',
    environment: process.env.REACT_APP_ENVIRONMENT || 'development',
    version: APP_VERSION,
    timestamp: new Date().toISOString(),
  });

  const [backendHealth, setBackendHealth] = useState(null);
  const [awsStatus, setAwsStatus] = useState(null);
  const [databaseStatus, setDatabaseStatus] = useState(null);
  const [cloudflareStatus, setCloudflareStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check backend health using the enhanced API service
    const checkBackendHealth = async () => {
      try {
        setLoading(true);

        // Check basic health
        const healthData = await apiFetch('/health');
        setBackendHealth(healthData);

        // Check AWS status
        const awsData = await apiFetch('/aws/status');
        setAwsStatus(awsData);

        // Check database status
        const dbData = await apiFetch('/database/status');
        setDatabaseStatus(dbData);

        // Check Cloudflare verification
        const cfData = await apiFetch('/cloudflare/verify');
        setCloudflareStatus(cfData);

        setError(null);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    checkBackendHealth();
  }, []);

  const renderStatus = (status, label) => {
    if (!status)
      return (
        <p>
          <strong>{label}:</strong>{' '}
          <span style={{ color: 'red' }}>Not available</span>
        </p>
      );

    const isHealthy =
      status.status === 'healthy' ||
      status.status === 'connected' ||
      status.verified === true;
    const color = isHealthy ? 'green' : 'red';

    return (
      <p>
        <strong>{label}:</strong>
        <span style={{ color, fontWeight: 'bold' }}>
          {isHealthy ? '✅ Healthy' : '❌ Unhealthy'}
        </span>
      </p>
    );
  };

  return (
    <div
      style={{
        fontFamily: 'Arial, sans-serif',
        margin: '40px',
        maxWidth: '800px',
      }}
    >
      <h1>Llamasee Health Check</h1>

      <div style={{ marginBottom: '30px' }}>
        <h2>Frontend Status</h2>
        <p>
          <strong>Status:</strong>{' '}
          <span style={{ color: 'green', fontWeight: 'bold' }}>
            {healthData.status}
          </span>
        </p>
        <p>
          <strong>Service:</strong> {healthData.service}
        </p>
        <p>
          <strong>Environment:</strong> {healthData.environment}
        </p>
        <p>
          <strong>Version:</strong> {healthData.version}
        </p>
        <p>
          <strong>Timestamp:</strong> {healthData.timestamp}
        </p>
        <p>
          <strong>API Base URL:</strong> <code>{getApiBaseUrl()}</code>
        </p>
        <p>
          <strong>NODE_ENV:</strong> <code>{process.env.NODE_ENV}</code>
        </p>
        <p>
          <strong>Hostname:</strong>{' '}
          <code>
            {typeof window !== 'undefined'
              ? window.location.hostname
              : 'server-side'}
          </code>
        </p>
        <p>
          <strong>Current URL:</strong>{' '}
          <code>
            {typeof window !== 'undefined'
              ? window.location.href
              : 'server-side'}
          </code>
        </p>
      </div>

      <div style={{ marginBottom: '30px' }}>
        <h2>Backend Status</h2>
        {loading ? (
          <p>Checking backend health...</p>
        ) : error ? (
          <div>
            <p>
              <strong>Error:</strong>{' '}
              <span style={{ color: 'red' }}>{error}</span>
            </p>
            <p>
              <strong>Status:</strong>{' '}
              <span style={{ color: 'red', fontWeight: 'bold' }}>
                ❌ Connection Failed
              </span>
            </p>
          </div>
        ) : (
          <div>
            {renderStatus(backendHealth, 'Basic Health')}
            {renderStatus(awsStatus, 'AWS Connection')}
            {renderStatus(databaseStatus, 'Database Connection')}
            {renderStatus(cloudflareStatus, 'Custom Header Verification')}

            {backendHealth && (
              <div style={{ marginTop: '15px' }}>
                <p>
                  <strong>Service:</strong> {backendHealth.service}
                </p>
                <p>
                  <strong>Version:</strong> {backendHealth.version}
                </p>
                <p>
                  <strong>Timestamp:</strong> {backendHealth.timestamp}
                </p>
              </div>
            )}

            {awsStatus && (
              <div style={{ marginTop: '15px' }}>
                <p>
                  <strong>AWS Region:</strong> {awsStatus.region}
                </p>
                <p>
                  <strong>Secrets Client:</strong> {awsStatus.secrets_client}
                </p>
                <p>
                  <strong>RDS Client:</strong> {awsStatus.rds_client}
                </p>
              </div>
            )}

            {databaseStatus && (
              <div style={{ marginTop: '15px' }}>
                <p>
                  <strong>Database Endpoint:</strong>{' '}
                  <code>{databaseStatus.database_endpoint}</code>
                </p>
              </div>
            )}

            {cloudflareStatus && (
              <div style={{ marginTop: '15px' }}>
                <p>
                  <strong>Client IP:</strong> {cloudflareStatus.client_ip}
                </p>
                <p>
                  <strong>Request ID:</strong> {cloudflareStatus.request_id}
                </p>
                <p>
                  <strong>Country:</strong> {cloudflareStatus.country}
                </p>
                <p>
                  <strong>Verification Level:</strong>{' '}
                  {cloudflareStatus.verification_details?.verification_methods
                    ? 'Full Verification'
                    : 'Basic Verification'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HealthCheck;
